import { Form, Spin, Checkbox, Alert, Radio, Button } from "antd";
import { Button as PrimeBtn } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";

import {
    updateProjectFeedback,
    getFeedbacks,
    updateAdjustmentData
} from "../../../apis/ProjectApis";
import { confirmDialog } from "primereact/confirmdialog";
import FMLCheckingDialog from "./FMLCheckingDialog";
import { highlightText } from "../../../utils/projectUtils";

const FeedbackDialog = (props) => {
    const [apiLoading, setApiLoading] = useState(false);
    const toast = useRef(null);
    const [form] = Form.useForm();
    const [feedbackOptions, setFeedbackOptions] = useState([]);

    const [reworkFeedbackOptions, setReworkFeedbackOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);

    const project = props.project;
    const [showWarning, setShowWarning] = useState(false);
    const [showReworkWarning, setShowReworkWarning] = useState(false);
    const [showCheckBoxes, setShowCheckBoxes] = useState(true);
    const [visibleFMLCheckingDialog, setVisibleFMLCheckingDialog] = useState(false);  

    useEffect(() => {
        if (project) {
            handleGetFeedbacks();
        }
    }, [project]);

    // reset when dialog becomes visible
    useEffect(() => {
        if (props.visible) {
            // clear warnings and reset form values
            setShowWarning(false);
            form.resetFields();
            // fetch latest options for current project (safe even if already fetched)
            if (project && project.id) {
                handleGetFeedbacks();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.visible]);

    const changeVisibleFMLCheckingDialog = (bool) => {
        setApiLoading(false);
        setVisibleFMLCheckingDialog(bool);
    };

    const handleGetFeedbacks = () => {
        if (project && project.id) {
            setApiLoading(true);
            getFeedbacks(project.id)
                .then((res) => {
                    if (res.status === 200) {
                        setApiLoading(false);
                        setFeedbackOptions(
                            res.data.filter(item => item.feedbackType !== "REWORK").map(item => ({
                                label: item.value,
                                value: item.key
                            }))
                        );

                        setReworkFeedbackOptions(
                            res.data.filter(item => item.feedbackType === "REWORK").map(item => ({
                                label: item.value,
                                value: item.key,
                            }))
                        );

                        // set rework yes/no + selected rework feedback option
                        if (props.project?.adjustment == null || props.project?.adjustment === "Without Cost") {
                            setSelectedValues("YES");
                        } else {
                            setSelectedValues("NO");

                            // Map project.adjustment to the correct feedback option key
                            const adjustment = props.project.adjustment.toLowerCase();
                            let reworkKey = null;

                            if (adjustment.includes("small")) {
                                reworkKey = "FEEDBACK_REWORK_OPTION_SMALL";
                            } else if (adjustment.includes("big")) {
                                reworkKey = "FEEDBACK_REWORK_OPTION_BIG";
                            } else if (adjustment.includes("remake")) {
                                reworkKey = "FEEDBACK_REWORK_OPTION_REMAKE";
                            }

                            if (reworkKey) {
                                form.setFieldsValue({ reworkFeedback: [reworkKey] });
                            }
                        }

                    } else {
                        toast.current.show({
                            severity: "error",
                            summary: "Status",
                            detail: res.response.data,
                            life: 3000,
                        });
                        setApiLoading(false);
                    }
                })
                .catch((e) => {
                    toast.current.show({
                        severity: "error",
                        summary: "Status",
                        detail: e,
                        life: 3000,
                    });
                    setApiLoading(false);
                });
        } else {
            toast.current.show({
                severity: "warn",
                summary: "Status",
                detail: "No Selected Project",
                life: 3000,
            });
        }
    };

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            let error = false;
            if (values?.generalFeedback?.length !== feedbackOptions?.length && showCheckBoxes == true) {
                setShowWarning(true);
                error = true;
            } else {
                setShowWarning(false);
            }

            if (props?.project?.parentId != null && ((values?.reworkFeedback?.length == 0 && selectedValues?.includes("NO"))
                || selectedValues?.length == 0)) {
                setShowReworkWarning(true);
                error = true;
            } else {
                setShowReworkWarning(false);
            }

            if (error){
                return;
            }

            if (showCheckBoxes == true && values?.generalFeedback[0] == "GENERAL_AGENDA_GO") {
                confirmDialog({
                    message: (
                        <>
                            Are you sure the m2 is correct?
                            <br />
                            {highlightText(feedbackOptions[0]?.label)}
                        </>
                    ),
                    header: "Confirmation",
                    icon: "pi pi-exclamation-triangle",
                    rejectLabel: "No",
                    acceptLabel: "Move status",
                    accept: () => {
                        updateReworkStatus(values);
                        props.setVisible(false, true);
                    },
                    reject: () => {
                        updateReworkStatus(values);
                        props.setVisible(false, false)
                    }
                });
            } else {
                updateReworkStatus(values);
                props.setVisible(false, true);
            }


        })
    }

    const updateReworkStatus = (values) => {
        if (reworkFeedbackOptions?.length == 0){
            return;
        }
        form.validateFields().then((values) => {
            setApiLoading(true);
            const projectValues = {
                id: project.id,
                adjustment: values?.reworkFeedback[0]?.length > 0 ?
                    values?.reworkFeedback[0]?.replace("FEEDBACK_REWORK_OPTION_", "").toLowerCase().replace(/^./, c => c.toUpperCase())
                    : "Without Cost"
            };
            updateAdjustmentData(projectValues).then((res) => {
                if (res.status === 200) {
                    setApiLoading(false);
                    props.setVisible(false);
                } else {
                    setApiLoading(false);
                    props.setVisible(false);
                }
            }).catch((e) => {
                toast.current.show({
                    severity: "error",
                    summary: "Status",
                    detail: e,
                    life: 3000,
                });
                setApiLoading(false);
            });
        })
    }

    return (
        <React.Fragment>
            <Toast ref={toast} />
            <Dialog
                style={{ width: "560px" }}
                header={"Feedback"}
                visible={props.visible}
                onHide={() => { props.setVisible(false) }}
            >
                <Spin spinning={apiLoading} >
                    <React.Fragment>
                        <div className='flex justify-content-between align-items-center'>
                            <Form form={form} onFinish={handleSubmit} >
                                {showWarning && (
                                    <Alert
                                        message="Please select all feedback options before submitting."
                                        type="warning"
                                        showIcon
                                        style={{marginBottom: "10px"}}
                                    />
                                )}
                                <Form.Item
                                    name="generalFeedback"
                                >
                                    <Checkbox.Group style={{ width: "500px" }}>
                                        {feedbackOptions.map(opt => (
                                            <div
                                                key={opt.value}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    whiteSpace: "pre-line",   // <-- key for \n support
                                                    wordWrap: "break-word",
                                                }}
                                            >
                                                {highlightText(opt.label)}
                                                {opt.value === "FML_CHECKING" &&<PrimeBtn
                                                    icon="pi pi-check-square"
                                                    className="p-button-rounded p-button-warning"
                                                    aria-label="FML Checking Error"
                                                    style={{backgroundColor: "none"}}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setVisibleFMLCheckingDialog(true);
                                                    }}
                                                />}
                                                {showCheckBoxes && <Checkbox value={opt.value} />}
                                            </div>
                                        ))}
                                    </Checkbox.Group>
                                </Form.Item>
                                {showReworkWarning && (
                                    <Alert
                                        message="Please select rework before submitting."
                                        type="warning"
                                        showIcon
                                        style={{marginBottom: "10px"}}
                                    />
                                )}
                                {reworkFeedbackOptions?.length > 0 &&
                                    <>
                                        <p style={{fontWeight: "bold"}}>Was the rework our fault?</p>
                                        <Radio.Group
                                            value={selectedValues}
                                            onChange={(e) => setSelectedValues(e.target.value)}
                                            style={{ width: "500px" }}
                                        >
                                            <Radio value="YES">Yes (Price = 0)</Radio>
                                            <Radio value="NO">No</Radio>
                                        </Radio.Group>
                                        <Form.Item
                                            name="reworkFeedback"
                                        >
                                            <Checkbox.Group
                                                style={{ width: "500px" }}>
                                                <br></br>
                                                {selectedValues.includes("NO") && reworkFeedbackOptions.map(opt => (
                                                    <div
                                                        key={opt.value}
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            whiteSpace: "pre-line",   // <-- key for \n support
                                                            wordWrap: "break-word",
                                                        }}
                                                    >
                                                        {opt.label}
                                                        <Checkbox value={opt.value} />
                                                    </div>
                                                ))}
                                            </Checkbox.Group>
                                        </Form.Item></>

                                }
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <PrimeBtn
                                        style={{
                                            backgroundColor: "#037eea",
                                            borderColor: "#037eea",
                                            float: "right",
                                            marginRight: "1rem",
                                        }}
                                        type="submit"
                                        label="Submit"
                                    />
                                </div>
                            </Form>
                        </div>
                    </React.Fragment>
                </Spin>

            </Dialog>

            <Dialog
                style={{ width: "35vw", height: "30vw" }}
                header="Warnings"
                visible={visibleFMLCheckingDialog}
                onHide={() => {
                    setVisibleFMLCheckingDialog(false);
                }}
            >
                <FMLCheckingDialog
                    project={project}
                    setVisible={changeVisibleFMLCheckingDialog}
                />
            </Dialog>
        </React.Fragment>
    )
}

export default FeedbackDialog
